import { createContext, FC, PropsWithChildren, useContext, useEffect, useRef, useState } from "react";

import { type AppUserState, AppUserStateOnboardingStateEnum } from "@asd/customer-portal-api";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import Keycloak from "keycloak-js";
import { useLocation, useNavigate } from "react-router-dom";

import { axiosInstance, UserService } from "../services";
import { env } from "../utils/env";
import { routeToErrorPage } from "../utils/navigationMap";

export type KeycloakType = Keycloak & {
  onVerificationCallback?: () => Promise<void>;
  userState: AppUserState;
  userInfo: {
    email: string;
    email_verified: boolean;
    family_name: string;
    given_name: string;
    name: string;
    phone_number: string;
    preferred_username: string;
    programs: string;
    sub: string;
  };
};

const KeycloakContext = createContext<KeycloakType>({} as KeycloakType);

export const useKeycloak = () => useContext(KeycloakContext);

const keycloakUrl = env.REACT_APP_KEYCLOAK_URL.startsWith("/")
  ? `${window.location.origin}${env.REACT_APP_KEYCLOAK_URL}`
  : env.REACT_APP_KEYCLOAK_URL;

const keycloakInstance = new Keycloak({
  url: keycloakUrl,
  realm: "customer-portal-realm",
  clientId: "customer-portal-frontend",
}) as KeycloakType;

const EVENTS = ["mousedown", "touchstart", "keydown", "click", "input", "focusin"];
const SESSION_TIMEOUT_MS = 1000 * 60 * 20; // 20 minutes
const SESSION_TIMEOUT_REMAINING_SECONDS = 60 * 5; // 5 minutes — must match WARNING_DURATION_MS in seconds
const WARNING_DURATION_MS = 1000 * SESSION_TIMEOUT_REMAINING_SECONDS;

const KeycloakProvider: FC<PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [keycloak, setKeycloak] = useState<KeycloakType>({} as KeycloakType);
  const [sessionTimeoutSecondsRemaining, setSessionTimeoutSecondsRemaining] = useState(-1);

  const autoLogoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionTimeoutTimer = useRef<NodeJS.Timeout | null>(null);
  const refreshTokenRef = useRef<NodeJS.Timeout | null>(null);
  const scheduleRef = useRef<() => void>(() => {});
  const warnAtRef = useRef<number | null>(null);
  const logoutAtRef = useRef<number | null>(null);
  const logoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const initialPath = location.pathname;

  const closeDialog = (logout: boolean = true) => {
    if (sessionTimeoutTimer.current) {
      clearInterval(sessionTimeoutTimer.current);
      sessionTimeoutTimer.current = null;
    }
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }

    if (logout) {
      keycloakInstance.logout();
    } else {
      setSessionTimeoutSecondsRemaining(-1);
      // eslint-disable-next-line no-use-before-define
      startAutoLogout();
    }
  };

  const startWarningCountdown = () => {
    if (sessionTimeoutTimer.current) {
      return;
    }
    const remaining = Math.max(0, Math.ceil((logoutAtRef.current! - Date.now()) / 1000));
    setSessionTimeoutSecondsRemaining(remaining);

    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
    }
    logoutTimeoutRef.current = setTimeout(() => {
      clearInterval(sessionTimeoutTimer.current!);
      sessionTimeoutTimer.current = null;
      closeDialog(true);
    }, logoutAtRef.current! - Date.now());

    sessionTimeoutTimer.current = setInterval(() => {
      const r = Math.max(0, Math.ceil((logoutAtRef.current! - Date.now()) / 1000));
      setSessionTimeoutSecondsRemaining(r);

      if (r <= 0) {
        clearInterval(sessionTimeoutTimer.current!);
        sessionTimeoutTimer.current = null;
        if (logoutTimeoutRef.current) {
          clearTimeout(logoutTimeoutRef.current);
          logoutTimeoutRef.current = null;
        }
        closeDialog(true);
      }
    }, 1000);
  };

  const startAutoLogout = () => {
    EVENTS.forEach((event) => document.removeEventListener(event, scheduleRef.current));

    scheduleRef.current = () => {
      if (sessionTimeoutTimer.current) {
        return;
      }

      if (autoLogoutRef.current) {
        clearTimeout(autoLogoutRef.current);
      }
      setSessionTimeoutSecondsRemaining(-1);

      const now = Date.now();
      warnAtRef.current = now + SESSION_TIMEOUT_MS;
      logoutAtRef.current = now + SESSION_TIMEOUT_MS + WARNING_DURATION_MS;

      autoLogoutRef.current = setTimeout(() => {
        startWarningCountdown();
      }, SESSION_TIMEOUT_MS);
    };

    EVENTS.forEach((event) => document.addEventListener(event, scheduleRef.current));

    scheduleRef.current();
  };

  const refreshToken = () => {
    if (refreshTokenRef.current) {
      clearTimeout(refreshTokenRef.current);
    }

    const expirationTimeInSeconds = keycloakInstance.tokenParsed!.exp!;

    const currentTimeInSeconds = Math.floor(Date.now() / 1000);
    const timeRemaining = Math.min(60000, (expirationTimeInSeconds - currentTimeInSeconds) * 1000 - 10000); // 10 seconds before expiration or at most 60 seconds

    refreshTokenRef.current = setTimeout(() => {
      keycloakInstance
        .updateToken(-1) // refresh the token
        .then(() => {
          axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${keycloakInstance.token}`;
          refreshToken();
        })
        .catch(() => {
          keycloakInstance.logout();
        });
    }, timeRemaining);
  };

  const afterUserLinked = (userState: AppUserState, redirectHome: boolean = false) => {
    keycloakInstance.updateToken(-1);

    if (redirectHome) {
      navigate("/", { replace: true });
    } else if (initialPath && initialPath !== "/") {
      navigate(initialPath, { replace: true });
    }

    refreshToken();
    startAutoLogout();

    setKeycloak({
      ...keycloakInstance,
      userState,
    } as KeycloakType);
  };

  const afterLogin = (authenticated: boolean = false, isRecursive: boolean = false) => {
    if (authenticated || keycloakInstance.authenticated) {
      (async () => {
        try {
          axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${keycloakInstance.token}`;
          axiosInstance.interceptors.request.use(
            (config) => {
              (async () => {
                try {
                  const refreshed = await keycloakInstance.updateToken(-1);
                  if (refreshed) {
                    refreshToken();
                  }
                } catch {
                  // This will result in the user auto-routing to the login page
                }
              })();

              return config;
            },
            (error) => Promise.reject(error),
          );

          const currentUserState = await UserService.getUserState();

          await keycloakInstance.loadUserInfo();

          if (currentUserState?.data?.onboardingState === AppUserStateOnboardingStateEnum.COMPLETE) {
            afterUserLinked(currentUserState.data);
          } else if (currentUserState?.data?.onboardingState === AppUserStateOnboardingStateEnum.NEEDS_LINK) {
            if (currentUserState.data.legalAgreementStatus?.agreedToLegalAgreements) {
              setKeycloak({
                ...keycloakInstance,
                userState: {
                  ...keycloakInstance.userState,
                  legalAgreementStatus: {
                    agreedToLegalAgreements: true,
                  },
                },
                userInfo: keycloakInstance.userInfo,
                logout: keycloakInstance.logout,
                onVerificationCallback: async () => {
                  await keycloakInstance.updateToken(-1);
                  const updatedUserState = await UserService.getUserState();

                  if (!updatedUserState?.data) {
                    routeToErrorPage();
                    return;
                  }

                  afterUserLinked(updatedUserState.data, true);
                },
              } as KeycloakType);
            } else {
              (async () => {
                try {
                  const response = await UserService.acceptUserLegalAgreements();
                  if (response?.status === 204) {
                    await UserService.getUserState();

                    setKeycloak({
                      ...keycloakInstance,
                      userState: {
                        ...keycloakInstance.userState,
                        legalAgreementStatus: {
                          agreedToLegalAgreements: true,
                        },
                      },
                      userInfo: keycloakInstance.userInfo,
                      logout: keycloakInstance.logout,
                      onVerificationCallback: async () => {
                        await keycloakInstance.updateToken(-1);
                        const updatedUserState = await UserService.getUserState();

                        if (!updatedUserState?.data) {
                          routeToErrorPage();
                          return;
                        }

                        afterUserLinked(updatedUserState.data, true);
                      },
                    } as KeycloakType);
                  } else {
                    throw new Error("Failed to accept legal agreements");
                  }
                } catch {
                  routeToErrorPage();
                }
              })();
            }
          }
        } catch {
          routeToErrorPage();
        }
      })();
    } else {
      if (!isRecursive) {
        keycloakInstance.login().then(() => {
          afterLogin(true);
        });
      } else {
        routeToErrorPage();
      }
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      if (!warnAtRef.current || !logoutAtRef.current) {
        return;
      }

      const now = Date.now();

      if (now >= logoutAtRef.current) {
        // Already past logout time while tab was hidden — log out immediately
        if (sessionTimeoutTimer.current) {
          clearInterval(sessionTimeoutTimer.current);
          sessionTimeoutTimer.current = null;
        }
        closeDialog(true);
        return;
      }

      if (now >= warnAtRef.current) {
        // Inside the warning window — show/update dialog with correct remaining time
        startWarningCountdown();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!keycloakInstance.didInitialize) {
      keycloakInstance
        .init({
          onLoad: "login-required",
          checkLoginIframe: false,
          flow: "standard",
        })
        .then(afterLogin)
        .catch(routeToErrorPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <KeycloakContext.Provider value={keycloak}>
      {children}
      <Dialog open={sessionTimeoutSecondsRemaining >= 0}>
        <DialogTitle variant="h3">Session Warning</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center", mt: 1 }}>
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress
              color="error"
              size={100}
              value={
                sessionTimeoutSecondsRemaining === -1
                  ? 100
                  : Math.round((sessionTimeoutSecondsRemaining / SESSION_TIMEOUT_REMAINING_SECONDS) * 100)
              }
              variant="determinate"
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body1">
                {sessionTimeoutSecondsRemaining === -1 ? (
                  "00:00"
                ) : (
                  <>
                    {String(Math.floor(sessionTimeoutSecondsRemaining / 60)).padStart(2, "0")}:
                    {String(sessionTimeoutSecondsRemaining % 60).padStart(2, "0")}
                  </>
                )}
              </Typography>
            </Box>
          </Box>
          <DialogContentText id="alert-dialog-description">
            Your current session is going to expire and you will be logged out.
            <br />
            <br />
            Do you want to extend your session?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              closeDialog(false);
            }}
          >
            Extend Session
          </Button>
          <Button
            onClick={() => {
              closeDialog();
            }}
            variant="outlined"
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </KeycloakContext.Provider>
  );
};

export default KeycloakProvider;
